import { getLanguageKeyForFilePath } from '../language-registry.js';
import { augmentFunctionTypeParamReferenceTags } from '../augment-function-type-param-references.js';
import { augmentTypeReferenceTags } from '../augment-type-reference-tags.js';
import { enrichTagsWithEnclosingScope } from '../enrich-tag-scope.js';
import { filterMemberAccessPropertyTags, augmentMemberReceiverTags } from '../member-access/index.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from '../yield-during-work.js';
import { yieldForBridgeInteractivePriority } from '../../files/browser/in-flight.js';
import { isSymbolIndexDeferredError } from '../symbol-index/deferred-indexing.js';
import type { SymbolTag } from '../types.js';
import { parseSourceRoot } from './parse-source-root.js';
import { runTagsQuery } from './run-tags-query.js';
import { tagsFromMatchesAsync } from './tags-from-matches-async.js';

export async function extractTagsFromSource(filePath: string, source: string): Promise<SymbolTag[]> {
  const languageKey = getLanguageKeyForFilePath(filePath);
  await yieldForBridgeInteractivePriority();
  const parsed = await parseSourceRoot(filePath, source);
  if (!parsed) return [];
  try {
    const yieldState = createCodeNavYieldState();
    const matches = await runTagsQuery(parsed.loaded, parsed.root);
    const { tags, nameNodes } = await tagsFromMatchesAsync(matches, yieldState);
    await yieldDuringCodeNavWork(yieldState);
    const filtered = filterMemberAccessPropertyTags(tags, nameNodes, languageKey);
    const withTypes = await augmentTypeReferenceTags(filtered, nameNodes, parsed.root);
    await yieldDuringCodeNavWork(yieldState);
    const withFunctionTypeParamRefs = await augmentFunctionTypeParamReferenceTags(withTypes, nameNodes, parsed.root);
    await yieldDuringCodeNavWork(yieldState);
    const withReceivers = await augmentMemberReceiverTags(withFunctionTypeParamRefs, nameNodes, parsed.root, languageKey);
    await yieldDuringCodeNavWork(yieldState);
    return await enrichTagsWithEnclosingScope(withReceivers, nameNodes);
  } catch (err) {
    if (isSymbolIndexDeferredError(err)) throw err;
    return [];
  } finally {
    parsed.cleanup();
  }
}
